import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHomeService } from '../hooks/useHomeService.ts';
import { homeBuild } from '../../config/home.build';
import { RenderSections } from '../components/SectionRenderer';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import type { AppDispatch } from '@/app/store/store';

type HomeSection = {
  priority?: number;
  key: string;
  eventLabel?: string;
  value: Record<string, unknown>;
  showInDesktop?: boolean;
  showInMobile?: boolean;
  showInOldHome?: boolean;
  layoutSideBySide?: boolean;
  pairWith?: string;
  separatorColor?: string;
};

function parseHomeSections(input: unknown): HomeSection[] {
  if (Array.isArray(input)) {
    return input as HomeSection[];
  }

  if (input && typeof input === 'object') {
    const maybeData = (input as Record<string, unknown>).data;

    if (Array.isArray(maybeData)) {
      return maybeData as HomeSection[];
    }

    if (typeof maybeData === 'string') {
      try {
        const parsed = JSON.parse(maybeData) as unknown;
        if (Array.isArray(parsed)) {
          return parsed as HomeSection[];
        }
      } catch {
        // Ignore parse errors; render no sections.
      }
    }
  }

  return [];
}

export default function HomePage() {
  const dispatch = useDispatch<AppDispatch>();
  const { service } = useMemo(() => homeBuild(dispatch), [dispatch]);
  const { homePageData } = useHomeService({ service });
  const { analytics, isInitialized } = useAnalytics();
  const hasTrackedViewRef = useRef(false);

  useEffect(() => {
    if (hasTrackedViewRef.current) return;
    if (!analytics || !isInitialized) return;

    try {
      analytics.trackEvent('Home Page Viewed');
      hasTrackedViewRef.current = true;
    } catch (error) {
      console.error('Error tracking Home Page Viewed:', error);
    }
  }, [analytics, isInitialized]);

  const sections = useMemo(() => {
    return parseHomeSections(homePageData?.data);
  }, [homePageData]);

  return (
    <div className="w-full">
      {/* Dynamically render all sections from API/JSON data */}
      <RenderSections sections={sections} />
    </div>
  );
}

